// ApplicationTypeDialog.tsx
// 申請種別の詳細設定ダイアログ

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { ApplicationType } from '../data/ApplicationType';
import ThemePreview from './ThemePreview';
import AddThemeDialog from './AddThemeDialog';

interface ApplicationTypeDialogProps {
  open: boolean;
  applicationType: ApplicationType | null;
  availableThemes: ApplicationType['theme'][]; // 親コンポーネントから渡される利用可能なテーマリスト
  onSave: (type: ApplicationType) => void;
  onCancel: () => void;
  onThemeAdded: () => void; // 新規テーマ追加後のコールバック、テーマリストを再取得
  onExited?: () => void; // ダイアログ閉じるアニメーション完了後のコールバック
}

// バリデーションエラーのインターフェース
interface ValidationErrors {
  typeName?: string;
  themeName?: string;
}

/**
 * 申請種別編集・新規追加ダイアログコンポーネント
 * - 種別名・説明・テーマを設定し、即時プレビューで確認できる
 * - props: open (ダイアログ表示状態), applicationType (編集対象データ、nullの場合は新規作成),
 *          availableThemes (選択可能なテーマ一覧), onSave (保存コールバック),
 *          onCancel (キャンセルコールバック), onThemeAdded (テーマ追加後コールバック)
 */
export default function ApplicationTypeDialog({
  open,
  applicationType,
  availableThemes,
  onSave,
  onCancel,
  onThemeAdded,
  onExited,
}: ApplicationTypeDialogProps) {
  // フォームデータ
  const [formData, setFormData] = useState<ApplicationType>({
    id: '',
    typeName: '',
    description: '',
    theme: {
      themeId: '',
      themeName: '',
      iconName: 'AssignmentTwoToneIcon',
      iconColor: '#1976d2',
      iconBgColor: '#e3f2fd',
      iconBdColor: '#90caf9',
      buttonColor: '#e3f2fd',
      fontColor: '#1565c0',
      buttonBdColor: '#90caf9',
      themeDescription: '',
    },
  });

  // バリデーションエラー
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // テーマ追加ダイアログの開閉状態
  const [openAddTheme, setOpenAddTheme] = useState(false);

  // 既存データの読み込み
  useEffect(() => {
    // ダイアログが開いている時のみデータを更新
    if (open) {
      if (applicationType) {
        setFormData(applicationType);
      } else if (availableThemes.length > 0) {
        // 新規作成の場合、初期値を設定（最初のテーマを選択）
        const defaultTheme = availableThemes[0];
        setFormData({
          id: '',
          typeName: '',
          description: '',
          theme: defaultTheme,
        });
      }
      // ダイアログを開く際にエラーをクリア
      setValidationErrors({});
    }
  }, [applicationType, open, availableThemes]);

  // 種別名変更
  const handleTypeNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, typeName: e.target.value });
    // 入力時にエラーをクリア
    if (validationErrors.typeName && e.target.value.trim()) {
      setValidationErrors({ ...validationErrors, typeName: undefined });
    }
  };

  // 説明変更
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, description: e.target.value });
  };

  // テーマ選択変更
  const handleThemeSelectChange = (themeId: string) => {
    const selectedTheme = availableThemes.find((t) => t.themeId === themeId);
    if (selectedTheme) {
      setFormData({
        ...formData,
        theme: selectedTheme,
      });
      // テーマ選択時にエラーをクリア
      if (validationErrors.themeName) {
        setValidationErrors({ ...validationErrors, themeName: undefined });
      }
    }
  };

  // 新規テーマ追加
  const handleAddTheme = () => {
    // AddThemeDialog 内で API 呼び出しが完了している
    // ここでは親コンポーネントに通知してデータを再取得させる
    onThemeAdded();
    setOpenAddTheme(false);
  };

  // 保存処理
  const handleSave = () => {
    // バリデーション
    const errors: ValidationErrors = {};

    if (!formData.typeName.trim()) {
      errors.typeName = '※種別名を入力してください';
    }
    if (!formData.theme.themeName.trim()) {
      errors.themeName = '※テーマを選択してください';
    }

    // エラーがある場合は保存しない
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSave(formData);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="lg"
        fullWidth
        TransitionProps={{
          onExited: onExited,
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight="bold" sx={{ color: 'GrayText' }}>
              {applicationType ? '申請種別編集' : '申請種別新規追加'}
            </Typography>
            <EditIcon sx={{ fontSize: 20, color: 'GrayText' }} />
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* 左側: 種別情報 + テーマ選択 */}
            <Grid size={{ xs: 12, md: 7 }}>
              {/* 種別情報セクション */}
              <Box sx={{ mb: 3 }}>
                {/* <Typography variant="h6" gutterBottom>
                  種別情報
                </Typography>
                <Divider sx={{ mb: 2 }} /> */}

                <TextField
                  fullWidth
                  label="種別名"
                  required
                  value={formData.typeName}
                  onChange={handleTypeNameChange}
                  error={!!validationErrors.typeName}
                  helperText={validationErrors.typeName}
                  sx={{
                    mb: 2,
                    '& .MuiFormHelperText-root': {
                      fontSize: '1rem'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="説明"
                  multiline
                  rows={3}
                  value={formData.description || ''}
                  onChange={handleDescriptionChange}
                />
              </Box>

              {/* テーマ選択セクション */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  テーマ選択
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <FormControl fullWidth required error={!!validationErrors.themeName}>
                    <InputLabel required>テーマ</InputLabel>
                    <Select
                      value={formData.theme.themeId}
                      label="テーマ"
                      onChange={(e) => handleThemeSelectChange(e.target.value)}
                      error={!!validationErrors.themeName}
                    >
                      {availableThemes.map((theme) => (
                        <MenuItem key={theme.themeId} value={theme.themeId}>
                          {theme.themeName}
                          {theme.themeDescription && ` - ${theme.themeDescription}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.themeName && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, fontSize: '1rem' }}>
                        {validationErrors.themeName}
                      </Typography>
                    )}
                  </FormControl>

                  <IconButton
                    color="primary"
                    onClick={() => setOpenAddTheme(true)}
                    sx={{ flexShrink: 0, mt: 1 }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Box>
              </Box>
            </Grid>

            {/* 右側: プレビュー */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ position: 'sticky', top: 10 }}>
                <ThemePreview
                  typeName={formData.typeName || '種別名未設定'}
                  theme={formData.theme}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel} color="inherit">
            キャンセル
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* テーマ追加ダイアログ */}
      <AddThemeDialog
        open={openAddTheme}
        typeName={formData.typeName || '種別名未設定'}
        onAdd={handleAddTheme}
        onCancel={() => setOpenAddTheme(false)}
      />
    </>
  );
}
